import { beaconConversion } from "@/lib/conversionBeacon";

/**
 * Lightweight conversion event tracking layer.
 * Fires to window.gtag (GA4) if available and logs to console in dev.
 *
 * Google Ads setup:
 *   1. In Google Ads → Tools → Conversions, create a "Submit lead form" action.
 *      Google gives you a snippet like: gtag('event','conversion',{ send_to: 'AW-123456789/AbCdEfGhIj' })
 *   2. Paste the `AW-XXXXXXXXX/Label` string into GOOGLE_ADS_CONVERSION_LABEL below.
 *   3. Also uncomment the matching gtag('config','AW-XXXXXXXXX') line in index.html.
 *   4. Form submissions will then report as Google Ads conversions automatically.
 *
 * GA4 → Google Ads import: every form submit also fires the standard `generate_lead`
 * event on both GA4 properties (G-MLP9L5BY3C and G-5TWF0HPYZN). You can import that
 * event as a conversion inside Google Ads (Tools → Conversions → Import → GA4).
 */

// Paste your full Google Ads conversion labels here once Google issues them.
// Format: "AW-123456789/AbCdEfGhIj". Leave empty to disable that conversion.
// Create ONE conversion action per goal in Google Ads (Tools → Conversions):
//   - "Submit lead form" → GOOGLE_ADS_CONVERSION_LABEL (form submits)
//   - "Phone calls from website" → GOOGLE_ADS_PHONE_CONVERSION_LABEL (tel: clicks)
export const GOOGLE_ADS_CONVERSION_LABEL = "";
export const GOOGLE_ADS_PHONE_CONVERSION_LABEL = "";

type EventName =
  | "form_submit"
  | "cta_click"
  | "phone_call"
  | "exit_intent_shown"
  | "estimate_request"
  | "review_request_sent"
  | "page_scroll_75"
  | "generate_lead"
  | "conversion";

interface EventParams {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export function trackEvent(name: EventName, params?: EventParams) {
  // GA4
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, params);
  }

  // Dev logging
  if (import.meta.env.DEV) {
    console.log(`[Track] ${name}`, params);
  }
}

/**
 * Fires a Google Ads conversion event. No-op if GOOGLE_ADS_CONVERSION_LABEL is empty.
 */
export function trackAdsConversion(
  value?: number,
  currency = "USD",
  sendTo: string = GOOGLE_ADS_CONVERSION_LABEL,
) {
  if (!sendTo) return;
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "conversion", {
    send_to: sendTo,
    value,
    currency,
  });
  if (import.meta.env.DEV) {
    console.log(`[Track] google_ads_conversion`, { sendTo, value, currency });
  }
}

/**
 * Tracks a CTA click with standard params.
 */
export function trackCTAClick(label: string, location: string) {
  trackEvent("cta_click", { label, location, category: "engagement" });
}

/**
 * Tracks a form submission as both a GA4 event and (if configured) a Google Ads conversion.
 *
 * Fires three things:
 *   - GA4 `form_submit` (custom event, backward compatible)
 *   - GA4 `generate_lead` (standard event, importable as a Google Ads conversion)
 *   - Google Ads `conversion` (if GOOGLE_ADS_CONVERSION_LABEL is set)
 */
export function trackFormSubmit(formName: string, source?: string, value = 50) {
  trackEvent("form_submit", { label: formName, category: "conversion", source, value });
  trackEvent("generate_lead", {
    label: formName,
    source,
    currency: "USD",
    value,
  });
  trackAdsConversion(value);
  beaconConversion("form_submit", { method: "form", label: formName, service: source, value });
}

/**
 * Tracks a phone call click as both a GA4 event and (if configured) a Google Ads conversion.
 *
 * Fires:
 *   - GA4 `phone_call` (custom event)
 *   - GA4 `generate_lead` with method=phone (standard event, importable to Google Ads)
 *   - Google Ads `conversion` against GOOGLE_ADS_PHONE_CONVERSION_LABEL (if set)
 */
export function trackPhoneCall(location: string, value = 50) {
  trackEvent("phone_call", { label: "(989) 268-7676", location, category: "conversion", value });
  trackEvent("generate_lead", {
    label: "phone_call",
    method: "phone",
    source: location,
    currency: "USD",
    value,
  });
  trackAdsConversion(value, "USD", GOOGLE_ADS_PHONE_CONVERSION_LABEL);
  beaconConversion("phone_tap", { method: "phone", location, value });
}

/**
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%).
 * Call once on page mount — it self-manages via IntersectionObserver-free scroll listener.
 */
export function initScrollDepthTracking() {
  if (typeof window === "undefined") return;

  const milestones = [25, 50, 75, 100];
  const fired = new Set<number>();

  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);

    for (const m of milestones) {
      if (pct >= m && !fired.has(m)) {
        fired.add(m);
        trackEvent("page_scroll_75", {
          label: `${m}%`,
          category: "engagement",
          scroll_depth: m,
        });
      }
    }
  };

  window.addEventListener("scroll", handler, { passive: true });
  return () => window.removeEventListener("scroll", handler);
}
